import React, { useState } from 'react';
import { Box, Button, ButtonGroup, Chip, IconButton, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Slider, Stack, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/AddOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import type { BrandKit } from '../../../services/editingApi';
import type { Anchor, EditList, TextEffect, TextEntrance, TextOverlay, TextStyle } from '../../../utils/editing/editList';
import { EDIT_LIMITS, FONT_FAMILIES, outputDurationMs } from '../../../utils/editing/editList';
import { addText, formatMs, removeItem, TEXT_PRESETS, updateItem } from '../editStudioModel';
import { Choice, PanelHint, PositionPicker, SecondsField } from './controls';

interface TextPanelProps {
  list: EditList;
  kit: BrandKit | null;
  outputMs: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: EditList, key?: string) => void;
  onSeek: (outputMs: number) => void;
}

const STYLE_LABELS: Record<TextStyle, string> = { title: 'Title', lowerThird: 'Your name (lower third)', plain: 'A line of text' };
const FONT_LABELS: Record<string, string> = { sans: 'Clean (sans)', serif: 'Classic (serif)', mono: 'Typewriter (mono)' };
const EFFECTS: Array<{ value: TextEffect; label: string }> = [{ value: 'outline', label: 'Outline' }, { value: 'shadow', label: 'Shadow' }, { value: 'none', label: 'None' }];
const ENTRANCES: Array<{ value: TextEntrance; label: string }> = [{ value: 'fade', label: 'Fade' }, { value: 'slide', label: 'Slide up' }, { value: 'pop', label: 'Pop' }];

/** The anchor with its column set to left, centre or right (the row kept). */
export function alignAnchor(anchor: Anchor, align: 'left' | 'center' | 'right'): Anchor {
  const row = anchor.startsWith('top') ? 'top' : anchor.startsWith('bottom') ? 'bottom' : 'middle';
  if (row === 'middle') return align === 'left' ? 'left' : align === 'right' ? 'right' : 'center';
  if (align === 'center') return row;
  return `${row}-${align}` as Anchor;
}

const alignOf = (text: TextOverlay): 'left' | 'center' | 'right' => {
  const a = text.position.anchor;
  if (a.endsWith('left') || a === 'left') return 'left';
  if (a.endsWith('right') || a === 'right') return 'right';
  return 'center';
};

/** Titles, your name and lines of text: added at the playhead, styled, placed by drag or by the grid. */
/** Whether the text is on screen and fully in (not mid-fade) at this moment. */
export const fullyShown = (item: { fromMs: number; toMs: number; fadeMs: number }, atMs: number): boolean =>
  atMs >= item.fromMs + Math.max(0, item.fadeMs) && atMs < item.toMs - Math.max(0, item.fadeMs);

/** A moment the text is fully on screen: just after it has come in. */
export const visibleMoment = (item: { fromMs: number; toMs: number; fadeMs: number }): number =>
  Math.min(item.fromMs + Math.max(0, item.fadeMs), item.fromMs + Math.max(0, (item.toMs - item.fromMs) / 2));

const TextPanel: React.FC<TextPanelProps> = ({ list, kit, outputMs, selectedId, onSelect, onChange, onSeek }) => {
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const total = outputDurationMs(list);
  const brand = kit?.primaryColor || '#3366CC';
  const text = list.texts.find((item) => item.id === selectedId) || null;
  const changeText = (change: Partial<TextOverlay>, key?: string) => { if (text) onChange({ ...list, texts: updateItem(list.texts, text.id, change) }, key); };
  const add = (style: TextStyle) => {
    const name = kit?.displayName || 'Your name';
    const next = addText(list, style, style === 'lowerThird' ? name : style === 'title' ? 'Your title' : 'Your text', outputMs, {
      color: '#FFFFFF',
      backgroundColor: style === 'lowerThird' ? `${brand.slice(0, 7)}E6` : undefined,
    });
    onChange(next);
    onSelect(next.texts[next.texts.length - 1]?.id ?? null);
    setMenu(null);
  };

  return (
    <Box>
      <PanelHint>A title, your name as a lower third, or any line, added at the playhead. Drag it on the picture to place it; drag its corner to resize.</PanelHint>
      <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={list.texts.length >= EDIT_LIMITS.maxTexts} onClick={(event) => setMenu(event.currentTarget)} data-testid="add-text">Add text</Button>
      <Menu open={Boolean(menu)} anchorEl={menu} onClose={() => setMenu(null)}>
        {(['title', 'lowerThird', 'plain'] as TextStyle[]).map((style) => (
          <MenuItem key={style} onClick={() => add(style)}>{STYLE_LABELS[style]}</MenuItem>
        ))}
      </Menu>
      {list.texts.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>Nothing on the picture yet. Add a title for the first seconds, or your name as a lower third.</Typography>
      ) : (
        <List dense disablePadding sx={{ mt: 1 }}>
          {list.texts.map((item) => (
            <ListItem key={item.id} disableGutters secondaryAction={<IconButton size="small" aria-label={`Remove text ${item.text}`} onClick={() => { onChange({ ...list, texts: removeItem(list.texts, item.id) }); if (selectedId === item.id) onSelect(null); }}><DeleteOutlineIcon fontSize="inherit" /></IconButton>}>
              <ListItemButton selected={item.id === selectedId} onClick={() => { onSelect(item.id); if (!fullyShown(item, outputMs)) onSeek(visibleMoment(item)); }} sx={{ borderRadius: 1 }}>
                <ListItemText primary={item.text || '(empty)'} secondary={`${STYLE_LABELS[item.style]} · ${formatMs(item.fromMs)} – ${formatMs(item.toMs)}`} primaryTypographyProps={{ variant: 'body2', noWrap: true }} secondaryTypographyProps={{ variant: 'caption' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      {text ? (
        <Stack spacing={1.5} sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }} data-testid="text-settings">
          <TextField size="small" label="Text" value={text.text} inputProps={{ maxLength: EDIT_LIMITS.maxTextLength, 'data-testid': 'text-content' }} onChange={(event) => changeText({ text: event.target.value }, `text-${text.id}`)} multiline maxRows={3} />
          {text.style === 'lowerThird' ? (
            <TextField size="small" label="Second line (role, handle)" value={text.secondaryText || ''} inputProps={{ maxLength: EDIT_LIMITS.maxTextLength }} onChange={(event) => changeText({ secondaryText: event.target.value || null }, `text2-${text.id}`)} />
          ) : null}
          <Box>
            <Typography variant="caption" color="text.secondary">Looks</Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {TEXT_PRESETS.map((preset) => (
                <Chip key={preset.id} size="small" variant="outlined" label={preset.label} onClick={() => changeText(preset.apply(text, brand))} data-testid={`preset-${preset.id}`} />
              ))}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Choice label="Font" value={text.font} options={FONT_FAMILIES.map((font) => ({ value: font, label: FONT_LABELS[font] }))} onChange={(font) => changeText({ font })} />
            <TextField size="small" label="Colour" type="color" value={text.color.slice(0, 7)} onChange={(event) => changeText({ color: event.target.value.toUpperCase() }, `colour-${text.id}`)} sx={{ width: 90 }} inputProps={{ 'aria-label': 'Text colour' }} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary">Behind it</Typography>
            <Chip size="small" label="Nothing" variant={text.backgroundColor ? 'outlined' : 'filled'} color={text.backgroundColor ? 'default' : 'primary'} onClick={() => changeText({ backgroundColor: null })} />
            <Chip size="small" label="Dark box" variant={text.backgroundColor === '#000000A0' ? 'filled' : 'outlined'} color={text.backgroundColor === '#000000A0' ? 'primary' : 'default'} onClick={() => changeText({ backgroundColor: '#000000A0' })} />
            <Chip size="small" label="Your colour" variant={text.backgroundColor === `${brand.slice(0, 7).toUpperCase()}E6` ? 'filled' : 'outlined'} color={text.backgroundColor === `${brand.slice(0, 7).toUpperCase()}E6` ? 'primary' : 'default'} onClick={() => changeText({ backgroundColor: `${brand.slice(0, 7).toUpperCase()}E6` })} icon={<Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: brand, ml: '6px !important' }} />} />
          </Stack>
          {!text.backgroundColor ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">Edge</Typography>
              {EFFECTS.map((effect) => (
                <Chip key={effect.value} size="small" label={effect.label} variant={text.effect === effect.value ? 'filled' : 'outlined'} color={text.effect === effect.value ? 'primary' : 'default'} onClick={() => changeText({ effect: effect.value })} />
              ))}
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Comes in</Typography>
            {ENTRANCES.map((entrance) => (
              <Chip key={entrance.value} size="small" label={entrance.label} variant={text.entrance === entrance.value ? 'filled' : 'outlined'} color={text.entrance === entrance.value ? 'primary' : 'default'} onClick={() => changeText({ entrance: entrance.value, fadeMs: text.fadeMs || 300 })} />
            ))}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Align</Typography>
            <ButtonGroup size="small">
              {([['left', <FormatAlignLeftIcon key="l" fontSize="inherit" />], ['center', <FormatAlignCenterIcon key="c" fontSize="inherit" />], ['right', <FormatAlignRightIcon key="r" fontSize="inherit" />]] as Array<['left' | 'center' | 'right', React.ReactNode]>).map(([align, icon]) => (
                <Tooltip key={align} title={`Align ${align}`}>
                  <Button aria-label={`Align ${align}`} variant={text.position.xPct === null && alignOf(text) === align ? 'contained' : 'outlined'} onClick={() => changeText({ position: { ...text.position, anchor: alignAnchor(text.position.anchor, align), xPct: null, yPct: null } })}>{icon}</Button>
                </Tooltip>
              ))}
            </ButtonGroup>
          </Stack>
          <PositionPicker value={text.position} onChange={(position) => changeText({ position })} withMargin={text.style !== 'title'} />
          <Box>
            <Typography variant="caption" color="text.secondary">Size: {text.sizePct}% of the height</Typography>
            <Slider size="small" min={EDIT_LIMITS.textSizePct.min} max={EDIT_LIMITS.textSizePct.max} step={0.5} value={text.sizePct} onChange={(_, v) => changeText({ sizePct: v as number }, `size-${text.id}`)} aria-label="Text size" />
          </Box>
          <Stack direction="row" spacing={1}>
            <SecondsField label="From" valueMs={text.fromMs} maxMs={total} onChange={(fromMs) => { if (fromMs !== null && fromMs < text.toMs) changeText({ fromMs }); }} />
            <SecondsField label="To" valueMs={text.toMs} maxMs={total} onChange={(toMs) => { if (toMs !== null && toMs > text.fromMs) changeText({ toMs }); }} />
            <SecondsField label="In/out" valueMs={text.fadeMs} maxMs={EDIT_LIMITS.maxFadeMs} onChange={(fadeMs) => { if (fadeMs !== null) changeText({ fadeMs }); }} />
          </Stack>
        </Stack>
      ) : null}
    </Box>
  );
};

export default TextPanel;
